import { zodResolver } from '@hookform/resolvers/zod'
import { Save, ShieldAlert, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import type { Branch, CustomerDetail } from '@/api/types'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { formatShortDate } from '@/lib/datetime'

import { amendmentResetsKyc, toCustomerWizardValues } from '../edit-values'
import { mapCustomerProblem } from '../field-errors'
import { useUpdateCustomer } from '../mutations'
import type { DuplicateCheck, DuplicateStatus } from '../use-duplicate-check'
import { useDuplicateCheck } from '../use-duplicate-check'
import { customerWizardSchema, toCustomerWriteRequest } from '../validation'
import type { CustomerWizardInput, CustomerWizardValues } from '../validation'
import { AddressStep } from '../wizard/address-step'
import { ConsentsStep } from '../wizard/consents-step'
import { ContactStep } from '../wizard/contact-step'
import { IdentityStep } from '../wizard/identity-step'
import { NextOfKinStep } from '../wizard/next-of-kin-step'
import { stepForField } from '../wizard-steps'

/**
 * Amending a customer file.
 *
 * Not a wizard. Capture is a sequence because the customer is standing there and the operator is
 * working through a card; an amendment is a visit to one field somebody already knows they are
 * changing, and making them walk six steps to reach it would be a worse screen, not a safer one.
 * The sections are the wizard's own, so the two screens cannot drift apart on what a valid record
 * looks like.
 *
 * The thing this screen exists to get right is the warning. Amending an approved file's Ghana Card
 * number or date of birth returns it to `PendingReview` — the server enforces that, because an
 * approval is given for a named person evidenced by a document and changing the evidence means the
 * approval no longer describes the file. That has real consequences: a member of a village banking
 * group whose file goes back for review shows as unapproved on the group's member list, and a new
 * account cannot be opened until a supervisor looks at it again. So it is said twice, before the
 * fact: live, as soon as either field differs, and again in the confirmation.
 */
export interface CustomerEditFormProps {
  customer: CustomerDetail
  branches: Branch[]
  onSaved: () => void
  onCancel: () => void
}

/**
 * The duplicate check, adapted for an amendment.
 *
 * The Ghana Card number on the record being edited is, of course, already held by a customer — this
 * one. Reporting that as a duplicate would train an operator to ignore the warning, which is the
 * one outcome a duplicate warning cannot afford. So a match on this customer's own id reads as
 * clear, and a match on anybody else's reads exactly as it does during capture.
 */
function useSelfAwareDuplicateCheck(
  field: 'ghanaCardNumber' | 'phoneNumber',
  customerId: string,
): DuplicateCheck {
  const duplicate = useDuplicateCheck(field)

  return {
    check: duplicate.check,
    statusFor: (value: string): DuplicateStatus => {
      const status = duplicate.statusFor(value)
      if (status === 'duplicate' && duplicate.existingCustomerId === customerId) {
        return 'clear'
      }
      return status
    },
    existingCustomerId:
      duplicate.existingCustomerId === customerId ? null : duplicate.existingCustomerId,
  }
}

export function CustomerEditForm({ customer, branches, onSaved, onCancel }: CustomerEditFormProps) {
  const [unattached, setUnattached] = useState<string[]>([])
  const [confirming, setConfirming] = useState(false)

  const form = useForm<CustomerWizardInput, unknown, CustomerWizardValues>({
    resolver: zodResolver(customerWizardSchema),
    mode: 'onBlur',
    defaultValues: toCustomerWizardValues(customer),
  })

  const ghanaCardDuplicate = useSelfAwareDuplicateCheck('ghanaCardNumber', customer.id)
  const phoneDuplicate = useSelfAwareDuplicateCheck('phoneNumber', customer.id)
  const update = useUpdateCustomer(customer.id)

  const ghanaCardNumber = useWatch({ control: form.control, name: 'ghanaCardNumber' })
  const dateOfBirth = useWatch({ control: form.control, name: 'dateOfBirth' })

  const changes = amendmentResetsKyc(customer, { ghanaCardNumber, dateOfBirth })
  const wasApproved = customer.kycStatus === 'Approved'
  const sendsBackForReview = wasApproved && changes.any

  const isDirty = form.formState.isDirty
  const isSubmitted = update.isSuccess

  // A reload or a closed tab is the one way an unsaved amendment can still be lost, so the browser
  // is asked to check. Nothing here can style that prompt, and nothing should try to.
  useEffect(() => {
    if (!isDirty || isSubmitted) {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }

    window.addEventListener('beforeunload', warn)
    return () => {
      window.removeEventListener('beforeunload', warn)
    }
  }, [isDirty, isSubmitted])

  const save = form.handleSubmit((values) => {
    setUnattached([])

    const request = toCustomerWriteRequest(values)

    /*
     * A consent the customer was never asked about must not come back from an amendment recorded
     * as refused.
     *
     * Capture sends all three every time, which is right at a counter with the customer present:
     * each statement was read out and each answer, including "no", was given. An amendment is not
     * that conversation. Sending a `false` for a consent this record has never carried would write
     * a fresh row saying the customer declined it today, with today's timestamp and this operator's
     * name against it — evidence of something that did not happen.
     *
     * So an untouched consent that was never captured is left out, while one that exists is always
     * sent: that is how a withdrawal gets recorded.
     */
    const alreadyCaptured = new Set(customer.consents.map((entry) => entry.consentType))
    request.consents = request.consents.filter(
      (entry) => alreadyCaptured.has(entry.consentType) || entry.granted,
    )

    update.mutate(request, {
      onSuccess: () => {
        setConfirming(false)
        onSaved()
      },
      onError: (error) => {
        setConfirming(false)

        const mapped = mapCustomerProblem(error)
        for (const { path, message } of mapped.fields) {
          form.setError(path, { type: 'server', message })
        }
        setUnattached(mapped.unattached)

        // The refusal is scrolled to rather than only shown: on this screen every section is on
        // one page, and a message attached to a field two screens down is a message nobody reads.
        const firstField = mapped.fields[0]
        if (firstField) {
          const section = stepForField(firstField.path)
          document
            .getElementById(`edit-section-${section?.id ?? 'identity'}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      },
    })
  })

  return (
    <form
      noValidate
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault()

        // The confirmation is only worth a click where there is something to confirm. An
        // amendment that changes nothing consequential saves directly.
        if (sendsBackForReview) {
          setConfirming(true)
          return
        }

        void save(event)
      }}
    >
      {sendsBackForReview ? (
        <Alert tone="warning">
          <TriangleAlert aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This amendment sends the file back for review</AlertTitle>
            <AlertDescription>
              {customer.fullName}&apos;s KYC file is approved
              {customer.kycApprovedAtUtc ? ` (${formatShortDate(customer.kycApprovedAtUtc)})` : ''}.
              Changing the{' '}
              {changes.ghanaCard && changes.dateOfBirth
                ? 'Ghana Card number and the date of birth'
                : changes.ghanaCard
                  ? 'Ghana Card number'
                  : 'date of birth'}{' '}
              changes the evidence the approval was given against, so saving returns the file to{' '}
              <span className="font-medium">pending review</span> and a supervisor has to approve it
              again. Amending a phone number, an address, or an occupation does not.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <section id="edit-section-identity" className="space-y-4 rounded-card border border-border-subtle bg-canvas p-4">
        <SectionHeading
          title="Identity"
          description="The name, date of birth, and Ghana Card exactly as they appear on the card."
        />
        <IdentityStep control={form.control} branches={branches} duplicate={ghanaCardDuplicate} />
      </section>

      <section id="edit-section-contact" className="space-y-4 rounded-card border border-border-subtle bg-canvas p-4">
        <SectionHeading
          title="Contact and livelihood"
          description="How the customer is reached, and the baseline account activity is judged against."
        />
        <ContactStep control={form.control} duplicate={phoneDuplicate} />
      </section>

      <section id="edit-section-address" className="space-y-4 rounded-card border border-border-subtle bg-canvas p-4">
        <SectionHeading
          title="Address"
          description="A residence address is required. A business address is captured where there is one."
        />
        <AddressStep control={form.control} />
      </section>

      <section id="edit-section-nextOfKin" className="space-y-4 rounded-card border border-border-subtle bg-canvas p-4">
        <SectionHeading
          title="Next of kin"
          description="Optional. An entry that is started must be complete enough to act on."
        />
        <NextOfKinStep control={form.control} />
      </section>

      <section id="edit-section-consents" className="space-y-4 rounded-card border border-border-subtle bg-canvas p-4">
        <SectionHeading
          title="Consents"
          description="Recorded against the wording version the customer was shown. Withdrawing marketing consent takes effect immediately; transactional alerts do not depend on it."
        />
        <ConsentsStep control={form.control} />
      </section>

      {update.isError ? <ProblemAlert error={update.error} /> : null}

      {unattached.length > 0 ? (
        <Alert tone="danger">
          <ShieldAlert aria-hidden />
          <div className="space-y-1">
            <AlertTitle>The server refused some values</AlertTitle>
            <AlertDescription>
              <ul className="list-inside list-disc space-y-0.5">
                {unattached.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 rounded-card border border-border-subtle bg-surface p-3">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={update.isPending}>
          Cancel
        </Button>

        <span className="mr-auto text-2xs text-muted">
          The whole record is saved, not only what changed — the endpoint takes the complete file so
          that it can never sit half-valid between calls.
        </span>

        <Button
          type="submit"
          variant={sendsBackForReview ? 'danger' : 'primary'}
          disabled={update.isPending || !isDirty}
        >
          <Save />
          {update.isPending
            ? 'Saving…'
            : sendsBackForReview
              ? 'Save and send for review'
              : 'Save changes'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="This returns the file to pending review"
        description={`${customer.fullName} is approved today. Saving these changes withdraws that approval.`}
        confirmLabel="Save and send for review"
        tone="danger"
        isPending={update.isPending}
        error={update.isError ? update.error : null}
        onConfirm={() => void save()}
      >
        <div className="space-y-3 text-sm text-body">
          <ul className="space-y-2">
            {changes.ghanaCard ? (
              <ChangeRow
                label="Ghana Card number"
                // A file taken on from paper may carry no card at all, and the confirmation has to
                // say so rather than show an empty side of the comparison.
                from={customer.ghanaCardNumber ?? 'None recorded'}
                to={ghanaCardNumber}
              />
            ) : null}
            {changes.dateOfBirth ? (
              <ChangeRow
                label="Date of birth"
                from={customer.dateOfBirth ? formatShortDate(customer.dateOfBirth) : '—'}
                to={dateOfBirth ? formatShortDate(dateOfBirth) : '—'}
              />
            ) : null}
          </ul>

          <p>
            While the file is pending review the customer cannot join a village banking group and no
            new account can be opened for them. A supervisor other than you has to approve it again —
            and the approver cannot be whoever submitted it, so if you make this change you will not
            be the one who reverses it.
          </p>
        </div>
      </ConfirmDialog>
    </form>
  )
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-0.5">
      <h2 className="text-base font-semibold text-heading">{title}</h2>
      <p className="text-sm text-muted">{description}</p>
    </div>
  )
}

function ChangeRow({ label, from, to }: { label: string; from: string; to: string }) {
  return (
    <li className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <p className="mt-1 flex flex-wrap items-center gap-2 font-mono text-sm">
        <span className="text-muted line-through">{from}</span>
        <span aria-hidden>→</span>
        <span className="font-semibold text-heading">{to}</span>
      </p>
    </li>
  )
}
